/**
 * AppScrollArea
 *
 * The single overlay scrollbar primitive for the app. Wraps Mantine
 * `<ScrollArea>` (its bar is an absolutely-positioned overlay themed in
 * global.css), so the scrollbar never takes layout space and never reserves a
 * gutter. Use this instead of native `overflow:auto` containers and raw
 * `<ScrollArea>`.
 *
 * Gutter contract: the scrolled content owns its symmetric horizontal padding;
 * the overlay bar floats inside that inset. Do NOT default `offsetScrollbars`
 * (it permanently reserves space). `fullBleedGutter` is self-sufficient — it
 * negates the parent's inline padding on the root AND re-insets the content
 * slot by the same token, so consumers add no compensating CSS.
 *
 * Vertical lists: pass `clampWidth` so children truncate instead of forcing a
 * horizontal scrollbar (cancels the `min-width: min-content` Autosize puts on
 * the content wrapper). `clampWidth` also defaults `offsetScrollbars="present"`
 * (override-able) to keep rows full-width until the list scrolls, then reserve
 * an 8px gutter beside them.
 *
 * @module components/ui/AppScrollArea
 */
import { memo } from 'react';
import { ScrollArea } from '@mantine/core';
import clsx from 'clsx';

import { SCROLLBAR_OVERLAY_TYPE, SCROLLBAR_SIZE } from '@/constants/ui';

import type {
  AppScrollAreaProps,
  ScrollGutterToken,
} from './AppScrollArea.types';
import classes from './AppScrollArea.module.css';

/**
 * Each gutter token maps to a pair: the `root` negative-margin class (pulls the
 * bar to the parent edge) and the `content` re-inset (right) padding class. The
 * two always travel together, so they live in one table.
 */
const FULL_BLEED_CLASSES: Record<
  ScrollGutterToken,
  { root: string | undefined; content: string | undefined }
> = {
  xs: { root: classes.fullBleedXs, content: classes.fullBleedPadXs },
  sm: { root: classes.fullBleedSm, content: classes.fullBleedPadSm },
  md: { root: classes.fullBleedMd, content: classes.fullBleedPadMd },
  lg: { root: classes.fullBleedLg, content: classes.fullBleedPadLg },
  xl: { root: classes.fullBleedXl, content: classes.fullBleedPadXl },
};

export const AppScrollArea = memo(function AppScrollArea({
  fill = false,
  maxHeight,
  fullBleedGutter,
  clampWidth = false,
  offsetScrollbars,
  type = SCROLLBAR_OVERLAY_TYPE,
  scrollbarSize = SCROLLBAR_SIZE,
  className,
  children,
  ...rest
}: AppScrollAreaProps) {
  const bleed = fullBleedGutter
    ? FULL_BLEED_CLASSES[fullBleedGutter]
    : undefined;

  const mergedClassName = clsx(fill && classes.fill, bleed?.root, className);

  // Content slot carries both the clamp override and the full-bleed re-inset.
  const contentClassName =
    clsx(clampWidth && classes.clampContent, bleed?.content) || undefined;

  // Vertical-list mode also implies a reserved-on-scroll gutter; the caller can
  // still override `offsetScrollbars` explicitly.
  const resolvedOffsetScrollbars =
    offsetScrollbars ?? (clampWidth ? 'present' : undefined);

  const sharedProps = {
    type,
    scrollbarSize,
    offsetScrollbars: resolvedOffsetScrollbars,
    className: mergedClassName,
    // An `undefined` content slot is a no-op for Mantine, so pass it always.
    classNames: { content: contentClassName },
    // Vertical-list mode: turn off horizontal scrolling so clamped children
    // truncate instead of forcing a horizontal scrollbar.
    ...(clampWidth && { scrollbars: 'y' as const }),
    ...rest,
  };

  if (maxHeight !== undefined) {
    return (
      <ScrollArea.Autosize mah={maxHeight} {...sharedProps}>
        {children}
      </ScrollArea.Autosize>
    );
  }

  return <ScrollArea {...sharedProps}>{children}</ScrollArea>;
});
